import React from 'react'
import { API } from "aws-amplify";
import { toast } from "react-toastify";
import { withRouter } from "react-router";
import { BarChart, ResponsiveContainer, Bar, XAxis, YAxis } from 'recharts';
import _ from "lodash";

import { AssetGeneralDisclosureIndicatorExtraLarge } from "@/icons/Assets"
import { NavBackButton } from "@/components/Buttons"
import moment from '@/common/momentConfig';
import SatisfactionPersonnelSurveyCell from "./SatisfactionPersonnelSurveyCell"
import SatisfactionPersonnelFeedbackSectionV2 from "./SatisfactionPersonnelFeedbackSectionV2"
import {isAiInlineSummarizationEnabled} from "@/shared/access";
import AiSummaryBox from "@/common/AiSummaryBox";

const SURVEYS_PER_PAGE = 10;

function SatisfactionPersonnelViewV2(props) {
    const { personnelId } = props.match.params;
    const query = new URLSearchParams(props.location.search);
    const DATE_FORMAT = "YYYY-MM-DD";
    const START_DATE_QUERY_PARAM = "startDate";
    const END_DATE_QUERY_PARAM = "endDate";
    let startDateMoment = query.has(START_DATE_QUERY_PARAM) ? moment(query.get(START_DATE_QUERY_PARAM), DATE_FORMAT) : moment().utc().subtract(30, 'day');
    let endDateMoment = query.has(END_DATE_QUERY_PARAM) ? moment(query.get(END_DATE_QUERY_PARAM), DATE_FORMAT) : moment().utc();

    const [surveyDetails, setSurveyDetails] = React.useState(null);
    const [feedbackOpen, setFeedbackOpen] = React.useState(true);
    const [startDate] = React.useState(startDateMoment);
    const [endDate] = React.useState(endDateMoment);
    const [surveysPage, setSurveysPage] = React.useState(1);
    const [paginationAnnouncement, setPaginationAnnouncement] = React.useState('');
    const [aiSummaryVisible, setAiSummaryVisible] = React.useState(false);

    React.useEffect(() => {
        loadDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        if (!surveyDetails) return;
        const surveyList = surveyDetails.surveys || [];
        const pages = Math.ceil(surveyList.length / SURVEYS_PER_PAGE);
        const loaded = Math.min(SURVEYS_PER_PAGE, surveyList.length);
        setPaginationAnnouncement(`Page 1 of ${pages}. ${loaded} of ${surveyList.length} surveys loaded.`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [surveyDetails]);

    React.useEffect(() => {
        if (!isAiInlineSummarizationEnabled()) return;
        API.get('Core', '/api/v1/ai-administration')
            .then(response => {
                const summaryEnabled = response.find(s => s.flag === 'AI_SUMMARY_SETTING');
                const excludeSatisfaction = response.find(s => s.flag === 'AI_SUMMARY_EXCLUDE_SATISFACTION');
                const isSummaryOn = summaryEnabled ? !!summaryEnabled.enabled : false;
                const isExcluded = excludeSatisfaction ? !!excludeSatisfaction.enabled : false;
                setAiSummaryVisible(isSummaryOn && !isExcluded);
            })
            .catch(() => setAiSummaryVisible(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onModalClose = () => {
        loadDetails();
    };

    const loadDetails = () => {
        API.get("Core", "/api/v1/survey-reports/cpss/personnel/" + personnelId, {
            queryStringParameters: {
                startDate: startDate.format(DATE_FORMAT),
                endDate: endDate.format(DATE_FORMAT)
            }
        }).then(response => {
            setSurveyDetails(response);
            setSurveysPage(1);
        }, err => {
            const message = err?.response?.data?.message || 'Unknown error';
            toast("Could not load survey details: " + message, { position: "top-center", type: "error" });
        });
    };

    if (!surveyDetails) {
        return (<span role="status" aria-live="polite">Loading...</span>);
    }

    const fetchSurveyBasedSatisfactionRatings = (survey) => {
        if (!survey || !survey.answers) return null;
        const scores = survey.answers
            .filter(a =>
                a.normalizedAnswer &&
                !isNaN(parseInt(a.normalizedAnswer)) && isFinite(a.normalizedAnswer) &&
                a.question && !isNaN(parseInt(a.question.high)) &&
                !a.ignored
            )
            .map(a => ({
                percentage: (parseInt(a.normalizedAnswer) / parseInt(a.question.high)).toFixed(2),
                formattedScore: `${a.normalizedAnswer}/${a.question.high}`,
                highestScore: parseInt(a.question.high)
            }))
            .sort((a, b) => a.percentage - b.percentage || b.highestScore - a.highestScore);

        if (scores.length === 0) return null;

        const uniquePercentages = [...new Set(scores.map(item => item.percentage))];
        if (uniquePercentages.length === 1) {
            return { type: 'same', value: scores[0].formattedScore };
        } else {
            const lowest = scores[0].formattedScore;
            const highestScoreGroup = scores.filter(s => s.percentage === scores[scores.length - 1].percentage);
            const highest = highestScoreGroup.sort((a, b) => b.highestScore - a.highestScore)[0].formattedScore;
            return { type: 'range', lowest, highest };
        }
    };

    const surveys = surveyDetails.surveys || [];
    const totalPages = Math.ceil(surveys.length / SURVEYS_PER_PAGE);
    const pagedSurveys = surveys.slice((surveysPage - 1) * SURVEYS_PER_PAGE, surveysPage * SURVEYS_PER_PAGE);

    const goToPage = (newPage) => {
        setSurveysPage(newPage);
        const loaded = Math.min(newPage * SURVEYS_PER_PAGE, surveys.length);
        setPaginationAnnouncement(`Page ${newPage} of ${totalPages}. ${loaded} of ${surveys.length} surveys loaded.`);
    };

    const fetchAiSummary = async () => {
        try {
            const response = await API.post("Core", `/api/v1/ai-summarization/satisfaction/personnel/${personnelId}`, {
                queryStringParameters: {
                    startDate: startDate.format(DATE_FORMAT),
                    endDate: endDate.format(DATE_FORMAT),
                },
                body: {},
            });
            return {
                success: response.success,
                response: response.response,
                responseType: 'html',
            };
        } catch (err) {
            return {
                success: false,
                response: '<p>Failed to generate summary. Please try again.</p>',
                responseType: 'html',
            };
        }
    };

    const aiSummaryKey = `${startDate.format(DATE_FORMAT)}|${endDate.format(DATE_FORMAT)}`;

    const chartData = _.map(
        _.sortBy(surveyDetails.ranges, r => r.startDate),
        r => ({ name: r.key, cpss: _.round(r.cpss * 100, 1) })
    );

    return (
        <div className="detailView satisfactionPersonnelView satisfactionPersonnelViewV2">
            <div className="navBar">
                <NavBackButton parentTitle="Satisfaction" parentLink="/satisfaction" />
                <div className="navTitle">
                    <div className="viewName">{surveyDetails.currentPeriodCPSS.name}</div>
                    <div className="viewSubtitle satisfactionPeriod">
                        <div className="dateValue">{startDate.format("MMMM Do YYYY")} - {endDate.format("MMMM Do YYYY")}</div>
                    </div>
                </div>
                <div className="separator"></div>
            </div>

            <div className="scrollView">
                <div className="tableView">

                    {aiSummaryVisible && (
                        <div className="tableSection" style={{paddingTop: '12px', paddingBottom: '10px'}}>
                            <div style={{maxHeight: '220px', overflowY: 'auto'}}
                                 tabIndex={0}>
                                <AiSummaryBox
                                    key={aiSummaryKey}
                                    containerId="personnel-satisfaction-ai-summary-box"
                                    onSummaryRequested={fetchAiSummary}
                                />
                            </div>
                        </div>
                    )}

                    {/* CPSS Bar Chart */}
                    <div className="tableSection sectionGraph">
                        <div className="cpssGraph">
                            <div className="grapScore">{_.round(surveyDetails.currentPeriodCPSS.cpss * 100, 1) + '%'} CPSS</div>
                            <div className="graphPeriod">
                                {moment(_.minBy(surveyDetails.ranges, r => r.startDate).startDate).format("MMMM Do YYYY")}
                                {' '}-{' '}
                                {moment(_.maxBy(surveyDetails.ranges, r => r.endDate).endDate).format("MMMM Do YYYY")}
                            </div>
                            <ResponsiveContainer height={400} aria-hidden="true">
                                <BarChart data={chartData} margin={{ bottom: 30, top: 0, left: 30, right: 0 }}>
                                    <XAxis dataKey="name" tick={{ fill: 'white' }} axisLine={{ stroke: 'transparent' }} />
                                    <YAxis tickFormatter={d => d + "%"} axisLine={{ stroke: 'transparent' }} tick={{ fill: 'white' }} />
                                    <Bar dataKey="cpss" fill="#72ba4d" barSize={100} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Surveys Section */}
                    <div className="tableSection sectionSurveys">
                        <div className="sectionHeader">
                            <h2 className="sectionTitle">Surveys ({surveys.length})</h2>
                        </div>
                        <div
                            className="surveysGrid"
                            role="group"
                            aria-label={`Surveys page ${surveysPage} of ${totalPages}, showing ${pagedSurveys.length} surveys`}
                        >
                            {pagedSurveys.map(survey => {
                                const time = moment(moment.utc(survey.createdAt)).local().calendar();
                                const ratings = fetchSurveyBasedSatisfactionRatings(survey);
                                return (
                                    <SatisfactionPersonnelSurveyCell
                                        key={survey.id}
                                        link=""
                                        caller={survey.contact.readableName}
                                        surveyName={survey.surveyName}
                                        timestamp={time}
                                        satisfaction={ratings || ''}
                                        surveyId={survey.id}
                                        onModalClose={onModalClose}
                                    />
                                );
                            })}
                        </div>
                        {surveys.length > 0 && (
                            <nav className="surveysPagination" aria-label="Surveys pagination">
                                <span className="sr-only" aria-live="polite" aria-atomic="true">
                                    {paginationAnnouncement}
                                </span>
                                <button
                                    className="paginationArrow"
                                    onClick={() => goToPage(surveysPage - 1)}
                                    disabled={surveysPage === 1}
                                    aria-label="Previous page"
                                >
                                    &#8249;
                                </button>
                                <span className="paginationLabel" aria-live="polite" aria-atomic="true">
                                    Page {surveysPage} of {totalPages}
                                </span>
                                <button
                                    className="paginationArrow"
                                    onClick={() => goToPage(surveysPage + 1)}
                                    disabled={surveysPage === totalPages}
                                    aria-label="Next page"
                                >
                                    &#8250;
                                </button>
                            </nav>
                        )}
                    </div>

                    {/* Feedback Section */}
                    <div className={`tableSection sectionFeedback ${feedbackOpen ? 'expanded' : ''}`}>
                        <div className="sectionHeader">
                            <h2 className="sectionTitle" id="feedbackSectionHeading">
                                Feedback
                                <span className="feedbackDateRange">
                                    {' '}(Past {endDate.diff(startDate, 'days')} Days: {startDate.format("MMM D")} - {endDate.format("MMM D")})
                                </span>
                            </h2>
                            <button
                                className="feedbackToggleBtn"
                                onClick={() => setFeedbackOpen(!feedbackOpen)}
                                aria-expanded={feedbackOpen}
                                aria-label="Feedback section"
                            >
                                <AssetGeneralDisclosureIndicatorExtraLarge aria-hidden="true" />
                            </button>
                        </div>
                        <div
                            id="feedbackSectionContent"
                            role="region"
                            aria-labelledby="feedbackSectionHeading"
                        >
                            {feedbackOpen && (
                                <SatisfactionPersonnelFeedbackSectionV2
                                    surveys={surveys}
                                    onModalClose={onModalClose}
                                />
                            )}
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}

export default withRouter(SatisfactionPersonnelViewV2);
